import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react-lite';

import useUI from 'hooks/use-ui';
import { CategoriesWithPotencies } from 'shared/constants';
import { useHasFilterableSpecials } from 'src/dispensary/hooks/use-has-filterable-specials';
import { useSpecialsRedesignEnabled } from 'src/dispensary/specials/use-specials-redesign-enabled';
import type { FilterVisibility, MenuFilterOptions, SidebarCategory, SidebarDispensary } from './types';

type UseFilterVisibilityInput = {
  category?: SidebarCategory | null;
  customCategory?: unknown;
  dispensary?: SidebarDispensary | null;
  menuFilterOptions: MenuFilterOptions;
  onCollectionsPage?: boolean;
  onSearchPage?: boolean;
  route?: string;
};

/**
 * Computes which filter blocks the host would render in the menu sidebar.
 * Single source of truth for both the stock sidebar and the SDK FilterSidePanel bridge.
 */
export function useFilterVisibility({
  category,
  customCategory,
  dispensary,
  menuFilterOptions,
  onCollectionsPage,
  onSearchPage,
  route,
}: UseFilterVisibilityInput): FilterVisibility {
  const flags = useFlags();
  const UI = useUI();
  const isOnSubcategoryPage = useObserver(() => UI.onSubcategoryPage);

  const { brands, subcategories, strainTypes, weights, effects, terpenes } = menuFilterOptions;
  const onBrandsPage = _.includes(route, 'brands');
  // Every item on a specials page/route (e.g. /specials, /specials/category/[category]) is
  // already a special, so the "Sales & Offers" filter is redundant there — hide it.
  const onSpecialsPage = _.includes(route, 'specials');
  const hidePotencyFilters = dispensary?.storeSettings?.hideFilters ?? false;
  const showSubcategories = !_.isEmpty(subcategories) && !onBrandsPage;

  const isMultiSelectSubcategoryFilterEnabled = flags['ecomm.multi-filter-subcategories.rollout'] ?? false;
  const specialsRedesignEnabled = useSpecialsRedesignEnabled();
  // Gated rollout: enables the category/subcategory filter for custom categories (e.g. "ALL"),
  // targetable by ecomm-dispensary/ecomm-enterprise LD context.
  const customCategorySubcategoryFiltersEnabled = flags['ecomm.custom-category-subcategory-filters.rollout'] ?? false;
  // Gated rollout: exposes the full global category list (not just the active category's
  // subcategories) in the sidebar on category pages, targetable by ecomm-dispensary/
  // ecomm-enterprise LD context. See PF-9862.
  const globalCategoriesSidebarEnabled = flags['ecomm.global-categories-sidebar.rollout'] ?? false;
  const { hasFilterableData } = useHasFilterableSpecials({ skip: !specialsRedesignEnabled });

  // Category pages (/products/[category]) are the only place the global category list applies:
  // search/collections/brands pages already render the global list via
  // showCategorySubcategoryFilter, and custom categories have their own gated path above.
  const showGlobalCategoryFilter = Boolean(
    globalCategoriesSidebarEnabled &&
      category?.key &&
      !onSearchPage &&
      !onCollectionsPage &&
      !onBrandsPage &&
      !customCategory
  );

  return {
    isMultiSelectSubcategoryFilterEnabled,
    onBrandsPage,
    showBrands: !_.isEmpty(brands),
    // Custom categories (e.g. "ALL") are gated behind customCategorySubcategoryFiltersEnabled:
    // the filter options are already scoped to this collection's own products server-side (see
    // menu-filters-v2), so there's no data-correctness risk, but rollout is flag-controlled so
    // it can be targeted/killed per dispensary or enterprise.
    showCategorySubcategoryFilter: Boolean(
      (Boolean(onSearchPage) || Boolean(onCollectionsPage) || onBrandsPage) &&
        (!customCategory || customCategorySubcategoryFiltersEnabled)
    ),
    showGlobalCategoryFilter,
    showEffects: !_.isEmpty(effects) && !onBrandsPage && !(dispensary?.storeSettings?.hideEffects ?? false),
    showPotencies: !hidePotencyFilters && _.includes(CategoriesWithPotencies, category?.key) && !onBrandsPage,
    showSpecialsFilter: specialsRedesignEnabled && hasFilterableData && !onSpecialsPage,
    showSubcategoriesFilterMultiSelect: isMultiSelectSubcategoryFilterEnabled && !isOnSubcategoryPage,
    // The global category filter, when shown, replaces the flat subcategory filter -- it
    // expands the active category's subcategories inline (see CategorySubcategoryFilter).
    showSubcategoryFilter: Boolean(
      showSubcategories && !onSearchPage && !onCollectionsPage && !customCategory && !showGlobalCategoryFilter
    ),
    showStrainTypes: !_.isEmpty(strainTypes) && !onBrandsPage,
    showTerpenes: !_.isEmpty(terpenes),
    showWeights: (weights?.length ?? 0) > 1 && !onBrandsPage,
  };
}
